import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ts, P } from '../api';
import { Page, Section, Stat, Loading, ErrorNote, Select, useLoad, useTS, when, Pill } from '../ui';

export default function TSDashboard() {
  const session = useTS();
  const { data, error, loading, reload } = useLoad(() => ts.dashboard());
  const rem = useLoad(() => ts.reminders());

  const setAvailability = async (availability) => {
    try { await ts.updateMe({ availability }); await session.refresh(); reload(); } catch (e) { toast.error(e.message); }
  };

  if (loading && !data) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  const t = data.today;
  const supervisor = session.can(P.SUPERVISE);
  return (
    <Page title={`Hi ${data.me.name?.split(' ')[0] || ''}`} subtitle={data.company.name}
      actions={(
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">My status</span>
          <Select value={data.me.availability || 'offline'} onChange={setAvailability} data-testid="ts-availability"
            options={[['online', 'Available'], ['busy', 'Busy'], ['offline', 'Offline']]} />
        </label>
      )}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ts-dashboard-stats">
        <Stat label="Calls today" value={t.calls.total} hint={`${t.calls.inbound} in · ${t.calls.outbound} out`} />
        <Stat label="Missed calls" value={t.calls.missed} hint={`${t.calls.ai_handled} handled by AI`} />
        <Stat label="Open callbacks" value={t.callbacks.open} hint={`${t.callbacks.overdue} overdue`} />
        <Stat label="New leads" value={t.leads.new} hint={`${t.leads.appointments} appointments`} />
      </div>
      {supervisor && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Staff online" value={`${data.staff_online} / ${data.staff_total}`} />
          <Stat label="Live calls" value={data.active_calls} />
          <Stat label="Numbers voice ready" value={`${data.numbers.voice_ready} / ${data.numbers.total}`} hint={`${data.numbers.assigned} assigned`} />
          <Stat label="Numbers texting" value={`${data.numbers.sms_active} / ${data.numbers.total}`} />
        </div>
      )}
      {!supervisor && t.mine && (
        <Section title="My day">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>Calls: <strong>{t.mine.calls ?? 0}</strong></div>
            <div>Connected: <strong>{t.mine.connected ?? 0}</strong></div>
            <div>Talk time: <strong>{t.mine.talk_minutes ?? 0} min</strong></div>
            <div>Callbacks done: <strong>{t.mine.callbacks_done ?? 0}</strong></div>
          </div>
        </Section>
      )}
      {!data.me.assigned_number_id && (
        <Section>
          <p className="text-sm text-amber-700 dark:text-amber-400">
            You don't have a company number yet. {session.can(P.NUMBERS) ? <Link className="underline" to="/telesales/staff">Assign one in Staff & Numbers.</Link> : 'Ask your administrator to assign one.'}
          </p>
        </Section>
      )}
      <Section title="Due soon" actions={<Link to="/telesales/callbacks" className="text-xs text-primary">Callback Center</Link>}>
        {(rem.data?.callbacks_due || []).length === 0 && (rem.data?.tasks || []).length === 0
          ? <p className="text-sm text-muted-foreground">Nothing due in the next few minutes.</p>
          : (
            <ul className="divide-y divide-border text-sm">
              {(rem.data?.callbacks_due || []).map(c => (
                <li key={c.id} className="py-1.5 flex justify-between gap-2">
                  <span>Call back {c.customer_name || c.customer_phone} — {c.reason}</span>
                  <span className="text-xs text-muted-foreground">{when(c.due_at)}</span>
                </li>
              ))}
              {(rem.data?.tasks || []).map(tk => (
                <li key={tk.id} className="py-1.5 flex justify-between gap-2">
                  <span><Pill tone="blue">Reminder</Pill> {tk.text || tk.title}</span>
                  <span className="text-xs text-muted-foreground">{when(tk.due_at)}</span>
                </li>
              ))}
            </ul>
          )}
      </Section>
    </Page>
  );
}
